import React, { useEffect, useMemo, useState } from 'react';
import { useSessionStorage } from 'usehooks-ts';
import { useRouter } from '@abyss/web/hooks/useRouter';
import { config } from '@abyss/web/tools/config';
import { createRouter } from '@abyss/web/tools/createRouter';
import { OverlayProvider } from '@abyss/web/ui/OverlayProvider';
import { RouterProvider } from '@abyss/web/ui/RouterProvider';
import { LoadingSpinner } from '@abyss/web/ui/LoadingSpinner';
import { createScript } from '@abyss/web/tools/script';
import { SessionInitStatus, StoreKeys } from '@/common/constants';
import { Theme } from '@/common/Theme/Theme';
import { PortalName } from '@/common/DataModel/PortalData';
import { PortalThemeProps } from '@/common/DataModel/PortalThemeProps';
import { getPortalProps, ThemeVersion } from '@/common/Theme/utils';
import { Routes } from '../../routes';
import { LagoonProvider } from '../../lagoon';
import { portalNameThemeMap } from '@/style/theme';
import { useSessionInit } from '@/hooks/useSessionInit';
import {
  EmptySSOPortalEligibilityData,
  SSOPortalEligibilityData,
} from 'api/src/dataModels/SSOPortalEligibilityData';
import LanguageProvider from '@/components/Language/LanguageProvider';
import { TimeoutModal } from '@/components/TimeoutModal/TimeoutModal';
import {
  loginNotRequired,
  isInvalidSession,
} from '@/components/TimeoutModal/utils';
import { SignedOutModal } from '@/components/SignedOutModal';
import initLiveChatVisibilityManager from '../../utils/liveChatVisibility.utils';
import { Reactor } from '@/common/ReactorProvider';

type Props = {
  children?: React.ReactNode;
};

export const App = ({ children }: Props) => {
  const lagoonEnv = config('LAGOON_ENV') ?? 'dev';
  const { getSearchParams, getLocation } = useRouter();
  const [searchParams] = getSearchParams();
  const router = useMemo(() => {
    return createRouter(Routes);
  }, []);

  const [portalProps, setPortalProps] = useState<PortalThemeProps>({
    appName: '',
    lob: '',
    portalName: PortalName.UMR,
    portalTheme: portalNameThemeMap[PortalName.UMR]?.[ThemeVersion.V1],
  });
  const [showSignedOutModal, setShowSignedOutModal] = useState<boolean>(false);

  const initStatus = useSessionInit(searchParams, router);
  const [ssoPortalEligibilityData] =
    useSessionStorage<SSOPortalEligibilityData>(
      StoreKeys.SSO_PORTAL_ELIGIBILITY_DATA,
      EmptySSOPortalEligibilityData
    );

  const showTimeoutModal = initStatus && !loginNotRequired();
  const isInitialized =
    initStatus != SessionInitStatus.NOT_INITIALIZED &&
    ssoPortalEligibilityData?.portalSource;

  const handleValidateSession = () => {
    const displaySignedoutModal = isInvalidSession();
    setShowSignedOutModal(displaySignedoutModal);
  };

  const popStateHandler = (event: PopStateEvent) => {
    // Prevent back navigation if signed out modal is shown
    if (showSignedOutModal) {
      event.preventDefault();
      event.stopPropagation();
      window.history.pushState(
        null,
        document.title,
        (getLocation() as Location)?.href
      );
    }
  };

  useEffect(() => {
    if (isInitialized) {
      const props: PortalThemeProps = getPortalProps(
        ssoPortalEligibilityData?.portalSource,
        ssoPortalEligibilityData?.portalIdentifier
      );
      setPortalProps(props);
    }
  }, [
    isInitialized,
    ssoPortalEligibilityData?.portalIdentifier,
    ssoPortalEligibilityData?.portalSource,
  ]);

  useEffect(() => {
    // Validate on window focus if signed out modal is not shown
    if (!showSignedOutModal) {
      window.addEventListener('focus', handleValidateSession);
    } else {
      window.history.pushState(
        null,
        document.title,
        (getLocation() as Location)?.href
      );
      window.addEventListener('popstate', popStateHandler);
    }

    return () => {
      window.removeEventListener('focus', handleValidateSession);
      window.removeEventListener('popstate', popStateHandler);
    };
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSignedOutModal]);

  useEffect(() => {
    createScript('adobe-query-script', config('ADOBE_QUERY_SCRIPT'));

    // Initialize the live chat visibility manager to hide chat icon when modals are open
    const cleanupLiveChat = initLiveChatVisibilityManager();

    return () => {
      cleanupLiveChat();
    };
  }, []);

  if (!portalProps.appName) {
    return (
      <LoadingSpinner
        ariaLoadingLabel="loading app"
        isLoading={true}
        size="$sm"
      />
    );
  }

  return (
    <React.Fragment>
      <Reactor />
      <LanguageProvider>
        <LagoonProvider
          app={portalProps.appName}
          env={lagoonEnv}
          lob={portalProps.lob}
        >
          <OverlayProvider>
            <Theme>
              <RouterProvider router={router}>{children}</RouterProvider>
              {showTimeoutModal && (
                <TimeoutModal
                  router={router}
                  initStatus={initStatus}
                  showSignedOutModal={showSignedOutModal}
                  setShowSignedOutModal={setShowSignedOutModal}
                />
              )}
              {showSignedOutModal && (
                <SignedOutModal
                  portalIdentifier={ssoPortalEligibilityData?.portalIdentifier}
                />
              )}
            </Theme>
          </OverlayProvider>
        </LagoonProvider>
      </LanguageProvider>
    </React.Fragment>
  );
};
